<template>
  <div class="forgot-password-container">
    <div class="forgot-password-card">
      <router-link to="/login" class="back-link">← Back to Login</router-link>

      <h2 class="forgot-password-title">Reset Your Password</h2>
      <p class="forgot-password-subtitle">
        Enter your email address and we'll send you instructions to reset your password.
      </p>
      <p class="forgot-password-note">
        <strong>Note:</strong> This must be the email address used on your application form.
      </p>

      <form @submit.prevent="handleResetPassword" class="forgot-password-form">
        <label>Email Address</label>
        <input
          type="email"
          v-model="email"
          placeholder="you@example.com"
          required
          :disabled="loading"
        />

        <button class="reset-button" :disabled="loading || !email">
          {{ loading ? 'Sending...' : 'Send Reset Link' }}
        </button>

        <p v-if="error" class="error-msg">{{ error }}</p>
        <p v-if="success" class="success-msg">{{ success }}</p>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { auth } from '@/firebase'
import { sendPasswordResetEmail } from 'firebase/auth'
import Swal from 'sweetalert2'

const email = ref('')
const error = ref('')
const success = ref('')
const loading = ref(false)
const router = useRouter()

const handleResetPassword = async () => {
  loading.value = true
  error.value = ''
  success.value = ''

  try {
    await sendPasswordResetEmail(auth, email.value)

    success.value = 'Password reset email sent! Check your inbox for instructions. If you don\'t see it, please check your spam folder.'

    await Swal.fire({
      icon: 'success',
      title: 'Email Sent',
      text: 'Check your email for password reset instructions. If you don\'t see it, please check your spam folder.',
      confirmButtonText: 'OK'
    })

    // Redirect to login after 2 seconds
    setTimeout(() => {
      router.push({ name: 'Login' })
    }, 2000)
  } catch (err) {
    console.error('Error sending password reset email:', err)

    if (err.code === 'auth/user-not-found') {
      error.value = 'No account found with this email address.'
    } else if (err.code === 'auth/invalid-email') {
      error.value = 'Please enter a valid email address.'
    } else if (err.code === 'auth/too-many-requests') {
      error.value = 'Too many attempts. Please try again later.'
    } else {
      error.value = 'Failed to send reset email. Please try again.'
    }

    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.value,
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.forgot-password-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.forgot-password-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 40px;
  max-width: 400px;
  width: 100%;
  position: relative;
}

.back-link {
  display: inline-block;
  margin-bottom: 20px;
  color: #5867dd;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: color 0.2s;
}

.back-link:hover {
  color: #3f51b5;
  text-decoration: underline;
}

.forgot-password-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.forgot-password-subtitle {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 28px 0;
}

.forgot-password-note {
  font-size: 13px;
  color: #d97706;
  background-color: #fef3c7;
  border-left: 4px solid #d97706;
  padding: 12px;
  border-radius: 4px;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.forgot-password-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.forgot-password-form label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.forgot-password-form input {
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.forgot-password-form input:focus {
  outline: none;
  border-color: #5867dd;
  box-shadow: 0 0 0 3px rgba(88, 103, 221, 0.1);
}

.forgot-password-form input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.reset-button {
  padding: 12px 24px;
  background-color: #5867dd;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 8px;
}

.reset-button:hover:not(:disabled) {
  background-color: #3f51b5;
}

.reset-button:active:not(:disabled) {
  background-color: #2c3aa0;
}

.reset-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-msg {
  color: #dc2626;
  font-size: 13px;
  margin: 0;
  padding: 12px;
  background-color: #fee2e2;
  border-radius: 4px;
  border-left: 4px solid #dc2626;
}

.success-msg {
  color: #059669;
  font-size: 13px;
  margin: 0;
  padding: 12px;
  background-color: #d1fae5;
  border-radius: 4px;
  border-left: 4px solid #059669;
}

@media (max-width: 480px) {
  .forgot-password-card {
    padding: 28px 20px;
  }

  .forgot-password-title {
    font-size: 20px;
  }
}
</style>
